import { bootstrapApplication } from '@angular/platform-browser';
import { appConfig } from './app/app.config';
import { AppComponent } from './app/app.component';
import * as Sentry from "@sentry/angular";
import { environment } from "@env";

const browserCompatibility = (window as any).__APRUV_BROWSER_COMPAT__ as
  | {
      isBlocked?: boolean;
      isBestEffort?: boolean;
      iosMajorVersion?: number | null;
      safariMajorVersion?: number | null;
    }
  | undefined;

const debugLoggingEnabled = !environment.production;
const debugLog = (...args: any[]): void => {
  if (debugLoggingEnabled) {
    console.log(...args);
  }
};

// Get package version for release tracking
// Get version from window (injected by CI/CD in index.html)
// Falls back to 'local-dev' for local development
const getVersion = (): string => {
  const version = (window as any).APRUV_VERSION;
  // Check if version was properly replaced by CI/CD (not the placeholder)
  if (version && !version.includes('__APRUV_VERSION__')) {
    return version;
  }
  return 'local-dev';
};

Sentry.init({
  dsn: "https://af7ae57c5cd66d140b043a49b6500a12@o4506995354894336.ingest.us.sentry.io/4509322978328576",
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  // Add release information for better tracking
  // Format: apruv-client@<git-sha> - must match what's uploaded to Sentry
  release: `apruv-client@${getVersion()}`,
  // Track environment to separate production from development errors
  environment: environment.production ? 'production' : 'dev', // Match the actual deployment environment
  // Defer heavy integrations to after bootstrap — added in .then() below
  integrations: [],
  // Performance monitoring sampling
  tracesSampleRate: environment.production ? 0.2 : 1.0,
  // Session Replay sampling (record errors 100%, sessions 5%)
  replaysSessionSampleRate: environment.production ? 0.05 : 1.0,
  replaysOnErrorSampleRate: 1.0,
  // Enable structured logs (Beta feature)
  _experiments: { 
    enableLogs: true,
    beforeSendLog: (log) => {
      // Filter out debug logs in production
      if (environment.production && log.level === "debug") {
        return null;
      }
      return log;
    }
  },
  // Enable before-send to modify/filter events before sending and debug version issues
  beforeSend(event) {
    // Debug version detection - warn if using local-dev or unreplaced placeholder
    if (!event.release || event.release.includes('local-dev') || event.release.includes('__APRUV_VERSION__')) {
      console.warn('⚠️ Sentry: Using local/dev version - sourcemaps may not work', {
        windowVersion: (window as any).APRUV_VERSION,
        release: event.release,
        url: window.location.href
      });
    }

    // Sample warning-level messages to avoid rate limiting (only send 10%)
    if (event.level === 'warning') {
      if (Math.random() > 0.1) {
        return null; // Drop 90% of warnings
      }
    }

    // Filter out common startup warnings that aren't actionable
    const message = event.message || '';
    if ( message.includes('Google Pay') ||
        message.includes('Apple Pay')) {
      return null; // Don't send these to Sentry
    }

    return event;
  }
});

debugLog('🚀 Starting Angular bootstrap...');

// -----------------------------------------------------------------------------
//  Chunk load / dynamic import hard-crash mitigation
// -----------------------------------------------------------------------------
//  Handles Safari “Importing a module script failed” as well as Chrome/Edge
//  ChunkLoadError promise rejections. We reload once per session after clearing
//  caches and present a fallback message if recovery already ran recently.

// Chunk recovery constants and utilities - exposed globally for Angular ErrorHandler
const CHUNK_RECOVERY = {
  TIMESTAMP_KEY: 'apruv_chunk_error_timestamp',
  RETRY_WINDOW_MS: 60000, // 1 minute window for retries
  KNOWN_IGNORED_HOSTNAMES: ['pay.google.com', 'google.com/pay'],
  ERROR_PATTERNS: [
    /Importing a module script failed/i,
    /ChunkLoadError/i,
    /Loading chunk/i,
    /Failed to fetch dynamically imported module/i
  ]
};

function isHardRefreshInProgress(): boolean {
  return !!(window as any).__APRUV_HARD_REFRESH_IN_PROGRESS__;
}

function safeSessionGetItem(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch (error) {
    console.warn('Unable to read sessionStorage during chunk recovery flow.', error);
    return null;
  }
}

function safeSessionSetItem(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch (error) {
    console.warn('Unable to write sessionStorage during chunk recovery flow.', error);
  }
}

function safeSessionRemoveItem(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    console.warn('Unable to remove sessionStorage item during chunk recovery flow.', error);
  }
}

function isChunkErrorIgnoredSource(filename: string | undefined | null): boolean {
  if (!filename) {
    return false;
  }
  return CHUNK_RECOVERY.KNOWN_IGNORED_HOSTNAMES.some(host => filename.includes(host));
}

function shouldHandleChunkError(message?: string, errorName?: string): boolean {
  if (errorName && errorName.toLowerCase().includes('chunk')) {
    return true;
  }
  if (!message) {
    return false;
  }
  return CHUNK_RECOVERY.ERROR_PATTERNS.some(pattern => pattern.test(message));
}

function showChunkErrorFallbackScreen(): void {
  document.body.innerHTML = `
    <div style="padding:2rem;text-align:center;font-family:sans-serif;max-width:600px;margin:0 auto;">
      <h2 style="color:#ef4444;">Unable to Load Application</h2>
      <p style="color:#666;margin:1.5rem 0;">The application failed to load after attempting recovery.</p>
      <p style="color:#666;margin:1.5rem 0;"><strong>Please try:</strong></p>
      <ol style="text-align:left;color:#666;line-height:1.8;">
        <li>Check your internet connection</li>
        <li>Clear your browser cache (Settings → Privacy → Clear Data)</li>
        <li>Close all browser tabs and reopen</li>
        <li>Try a different browser</li>
      </ol>
      <button onclick="location.href='/'" style="margin-top:1.5rem;padding:0.75rem 1.5rem;background:#3b82f6;color:white;border:none;border-radius:0.5rem;cursor:pointer;font-size:1rem;">
        Try Again
      </button>
      <p style="color:#999;margin-top:2rem;font-size:0.875rem;">If the problem persists, contact support</p>
    </div>
  `;
}

function performChunkRecovery(context: Record<string, any>): void {
  if (isHardRefreshInProgress()) {
    console.warn('Chunk recovery skipped because a hard refresh is already in progress.', context);
    return;
  }

  const now = Date.now();
  const lastReloadTime = safeSessionGetItem(CHUNK_RECOVERY.TIMESTAMP_KEY);

  if (lastReloadTime && (now - parseInt(lastReloadTime)) < CHUNK_RECOVERY.RETRY_WINDOW_MS) {
    console.warn('Chunk recovery already attempted recently, showing fallback UI');
    showChunkErrorFallbackScreen();
    return;
  }

  safeSessionSetItem(CHUNK_RECOVERY.TIMESTAMP_KEY, now.toString());
  console.warn('🔄 Chunk loading failed - attempting recovery reload...', context);

  // Capture additional context for diagnostics
  try {
    (window as any).Sentry?.addBreadcrumb?.({
      category: 'chunk-recovery',
      message: 'Chunk recovery triggered',
      level: 'warning',
      data: context
    });
    (window as any).Sentry?.captureMessage?.('Chunk load failure - forcing reload', {
      level: 'warning',
      extra: context
    });
  } catch (_) {}

  try {
    if (window.indexedDB) {
      indexedDB.deleteDatabase('uppy-blobs');
      indexedDB.deleteDatabase('keyval-store');
    }

    const keycloakTokens = localStorage.getItem('kc-token');
    const keycloakRefresh = localStorage.getItem('kc-refresh');
    localStorage.clear();
    if (keycloakTokens) localStorage.setItem('kc-token', keycloakTokens);
    if (keycloakRefresh) localStorage.setItem('kc-refresh', keycloakRefresh);

    const tempTimestamp = safeSessionGetItem(CHUNK_RECOVERY.TIMESTAMP_KEY);
    sessionStorage.clear();
    if (tempTimestamp) safeSessionSetItem(CHUNK_RECOVERY.TIMESTAMP_KEY, tempTimestamp);
  } catch (e) {
    console.error('Error during cache cleanup:', e);
  }

  setTimeout(() => {
    // Preserve hash fragment (important for OAuth callbacks with responseMode: 'fragment')
    // Append cache-busting param without replacing existing query parameters
    const url = new URL(window.location.href);
    url.searchParams.set('_cb', Date.now().toString());
    window.location.href = url.toString();
  }, 50);
}

// Expose chunk recovery utilities globally for Angular ErrorHandler to use
(window as any).__APRUV_CHUNK_RECOVERY__ = {
  shouldHandleChunkError,
  performChunkRecovery,
  isIgnoredSource: isChunkErrorIgnoredSource
};

// Register window event listeners for chunk errors
(function registerChunkLoadErrorHandler () {
  function performRecoveryWithContext(eventContext: { message?: string; filename?: string; lineno?: number; colno?: number; error?: any; source: string }): void {
    performChunkRecovery({
      source: eventContext.source,
      message: eventContext.message,
      filename: eventContext.filename,
      lineno: eventContext.lineno,
      colno: eventContext.colno,
      errorName: eventContext.error?.name,
      userAgent: navigator.userAgent
    });
  }

  window.addEventListener('error', (ev: ErrorEvent) => {
    if (!ev) {
      return;
    }

    if (isChunkErrorIgnoredSource(ev.filename)) {
      ev.preventDefault?.();
      return;
    }

    if (!shouldHandleChunkError(ev.message, ev.error?.name)) {
      return;
    }

    ev.preventDefault?.();
    performRecoveryWithContext({
      source: 'window:error',
      message: ev.message,
      filename: ev.filename,
      lineno: ev.lineno,
      colno: ev.colno,
      error: ev.error
    });
  }, true);

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const reason = event?.reason;
    const message = typeof reason === 'string' ? reason : reason?.message;
    const name = reason?.name;
    const filename = (reason && reason?.request?.url) || undefined;

    if (isChunkErrorIgnoredSource(filename)) {
      console.info('Chunk error ignored for known third-party script:', filename);
      return;
    }

    if (!shouldHandleChunkError(message, name)) {
      return;
    }

    event?.preventDefault?.();
    performRecoveryWithContext({
      source: 'window:unhandledrejection',
      message,
      filename,
      error: reason
    });
  });

  window.addEventListener('load', () => {
    const lastReloadTime = safeSessionGetItem(CHUNK_RECOVERY.TIMESTAMP_KEY);
    if (lastReloadTime) {
      const timeSinceReload = Date.now() - parseInt(lastReloadTime);
      if (timeSinceReload > 5000) {
        safeSessionRemoveItem(CHUNK_RECOVERY.TIMESTAMP_KEY);
        console.log('✅ App loaded successfully after chunk error recovery');
      }
    }
  });
})();

debugLog('📱 About to bootstrap Angular application...');

if (browserCompatibility?.isBlocked) {
  console.warn('Skipping Angular bootstrap for blocked browser compatibility state.', browserCompatibility);
} else {
  bootstrapApplication(AppComponent, appConfig)
    .then(() => {
      debugLog('✅ Angular application bootstrapped successfully!');

      // Add heavy Sentry integrations after first paint to avoid blocking bootstrap
      Sentry.addIntegration(Sentry.browserTracingIntegration());
      Sentry.addIntegration(Sentry.replayIntegration());
      Sentry.addIntegration(Sentry.consoleLoggingIntegration({ levels: ["log", "error", "warn"] }));
    })
    .catch((err) => {
      console.error('🚨 Angular bootstrap failed:', err);
      Sentry.captureException(err);
    });
}
